import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useData } from '../contexts/DataContext';
import StatusMessage from '../components/CategoryDescription/StatusMessage';
import { ErrorBoundary } from '../components/shared/ErrorBoundary';
import ChatList from '../components/ChatList/ChatList';
import ChatWindow from '../components/ChatWindow/ChatWindow';
import DomainSelector from '../components/shared/DomainSelector';
import { ChatsProvider } from '../contexts/ChatsContext';
import { Domain } from '../types/data';
import FeatureFlagOverlay from '../components/shared/FeatureFlagOverlay';
import { FeatureFlag } from '../utils/featureFlags';

const Chat: React.FC = () => {
  const { domains, targetCompany, customerTargetGroups, fetchRelatedDomainData, fetchDomains } =
    useData();
  const [activeDomain, setActiveDomain] = useState<Domain | null>(null);
  const [selectedCustomerGroup, setSelectedCustomerGroup] = useState('');
  const { t } = useTranslation();
  const [isChatListVisible, setIsChatListVisible] = useState(false);

  useEffect(() => {
    fetchDomains();
  }, []);

  useEffect(() => {
    loadData();
  }, [domains, targetCompany]);

  useEffect(() => {
    if (customerTargetGroups.length === 1) {
      setSelectedCustomerGroup(customerTargetGroups[0].name);
    }
  }, [customerTargetGroups]);

  const loadData = () => {
    if (targetCompany && domains.length > 0) {
      const foundDomain = domains.find((domain) => domain.company_id === targetCompany);
      if (foundDomain) {
        setActiveDomain(foundDomain);
        fetchRelatedDomainData(foundDomain.id);
      } else {
        setActiveDomain(null);
      }
    }
  };

  const customerGroup = customerTargetGroups.find((group) => group.name === selectedCustomerGroup);

  if (!targetCompany || !activeDomain) {
    return <StatusMessage targetCompany={targetCompany} activeDomain={activeDomain} />;
  }

  return (
    <FeatureFlagOverlay feature={FeatureFlag.CHAT}>
      <div className="h-screen flex flex-col" style={{ height: 'calc(100vh - 35px)' }}>
        <div className="p-4 border-b " id="chat-header">
          <h1 className="text-3xl font-bold mb-2">{t('chat.title')}</h1>
          <div className="flex items-center mt-4 text-sm">
            <DomainSelector activeDomain={activeDomain} setActiveDomain={setActiveDomain} />
            <span className="mx-4">|</span>
            <div className=" flex items-center">
              <span className="block text-gray-600 mr-2 text-sm">
                <strong>{t('common.target_customer_group')}:</strong>
              </span>
              <select
                value={selectedCustomerGroup}
                onChange={(e) => setSelectedCustomerGroup(e.target.value)}
                className="inline-block min-w-[150px] p-2 border rounded-md"
              >
                <option value="">{t('category.select_customer_group')}</option>
                {customerTargetGroups.map((group) => (
                  <option key={group.name} value={group.name}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex-1 flex min-h-0">
          <ErrorBoundary>
            <ChatsProvider
              domainId={activeDomain?.id || null}
              customerGroupId={customerGroup?.id || null}
            >
              <div
                className={`transition-all duration-300 ${isChatListVisible ? 'w-64' : 'w-0 overflow-hidden'} flex-shrink-0`}
              >
                {isChatListVisible && <ChatList />}
              </div>
              <div
                onClick={() => setIsChatListVisible(!isChatListVisible)}
                className="cursor-pointer p-2 flex items-center justify-center bg-gray-100 hover:bg-gray-200"
                style={{
                  width: '24px',
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed',
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  letterSpacing: '0.05em',
                }}
              >
                {isChatListVisible ? 'hide chat list' : 'show chat list'}
              </div>
              <ErrorBoundary>
                <ChatWindow
                  selectedCustomerGroup={selectedCustomerGroup}
                  activeDomain={activeDomain}
                  targetCompany={targetCompany}
                  customerTargetGroups={customerTargetGroups}
                />
              </ErrorBoundary>
            </ChatsProvider>
          </ErrorBoundary>
        </div>
      </div>
    </FeatureFlagOverlay>
  );
};

export default Chat;
